<template>
    <v-card
        v-if="filteredFields.length"
        :class="['pa-6 client-info-card', topMargin ? 'mt-2' : 'mt-0']"
        :elevation="$vuetify.breakpoint.mdAndUp ? '0' : ''"
    >
        <v-row v-if="groupedField.group_name">
            <v-col class="pt-0 card-title d-flex justify-space-between">
                {{ groupedField.group_name }}
                <v-btn v-if="$vuetify.breakpoint.mdAndUp" color="n3" icon x-small @click="$emit('edit_click')">
                    <v-icon>edit</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-for="(row, index) in filteredFields" :key="row.name" class="align-baseline">

            <template v-if="row.field.type === 'contacts' && client.contacts.length">
                <v-col>
                    <ClientContactsList :client="client"/>
                </v-col>
            </template>
            <template v-else-if="row.field.type === 'emails'">
                <v-col>
                    <ClientEmailsList :client="client"/>
                </v-col>
            </template>
            <template v-else-if="row.field.type.startsWith('suppliers.')">
                <v-col>
                    <FieldGroupChilds :items="row.value" :parent-field="row.field" :account-id="client.account_id"/>
                </v-col>
            </template>

            <template v-else-if="row.field.name === 'address_street'">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <v-col cols="7" class="value pb-1 pt-1" :test-id="`${row.name}-value`">
                    {{ getAddress }}
                </v-col>
            </template>

            <template v-else-if="row.field.name === 'parent_id' && row.field.related_type === 'client'">
                <v-col cols="5" class="label pb-1 pt-1" v-if="client.parent_client">
                    {{ row.field.title }}
                </v-col>
                <v-col cols="7" class="value pb-1 pt-2 text-truncate" :test-id="`${row.name}-value`" v-if="client.parent_client">
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <div v-on="on" style="display: inline">
                                <router-link
                                    :to="{ name: 'client_profile', params: { id: row.value }}"
                                    style="text-decoration: none"
                                    class="text-truncate text--primary"
                                >
                                    <ClientAvatar :initials="client.parent_client.client_initials || ''" :color="client.parent_client.color || ''" clickable :size="24"></ClientAvatar>
                                    {{client.parent_client.name}}
                                </router-link>
                            </div>
                        </template>
                        {{ client.parent_client.name }}
                    </v-tooltip>
                </v-col>
                <v-col cols="5" class="label pb-1 pt-1" v-if="subClients.length > 0">
                    {{ $t("Sub-companies") }}
                </v-col>
                <v-col cols="7" class="value pb-2 pt-1" :test-id="`${row.name}-value`" v-if="subClients.length > 0">
                    <v-row v-for="(subclient, index) in client.subclients.slice(0, 3)" :key="index">
                        <v-col cols="12" class="py-1" style="overflow: hidden; text-overflow: ellipsis;">
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <div v-on="on" style="display: inline">
                                        <router-link
                                            :to="{ name: 'client_profile', params: { id: subclient.id }}"
                                            style="text-decoration: none"
                                            class="text-truncate text--primary"
                                        >
                                            <ClientAvatar :initials="subclient.client_initials || ''" :color="subclient.color || ''" clickable :size="24"></ClientAvatar>
                                            {{subclient.name}}
                                        </router-link>
                                    </div>
                                </template>
                                {{ subclient.name }}
                            </v-tooltip>
                        </v-col>
                    </v-row>
                    <v-expansion-panels accordion :value="showMoreSubClients ? 0 : null">
                        <v-expansion-panel>
                            <v-expansion-panel-content eager>
                                <v-row v-for="(subclient, index) in client.subclients.slice(3, client.subclients.length)" :key="index">
                                    <v-col cols="12" class="py-1" style="overflow: hidden; text-overflow: ellipsis;">
                                        <v-tooltip bottom>
                                            <template #activator="{ on }">
                                                <div v-on="on" style="display: inline">
                                                    <router-link
                                                        :to="{ name: 'client_profile', params: { id: subclient.id }}"
                                                        style="text-decoration: none"
                                                        class="text-truncate text--primary"
                                                    >
                                                        <ClientAvatar :initials="subclient.client_initials || ''" :color="subclient.color || ''" clickable :size="24"></ClientAvatar>
                                                        {{subclient.name}}
                                                    </router-link>
                                                </div>
                                            </template>
                                            {{ subclient.name }}
                                        </v-tooltip>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-btn
                        v-if="client.subclients.length > 3"
                        text
                        small
                        depressed
                        color="primary"
                        class="mt-1 ml-n3 mb-5"
                        @click="toggleShowAllSubClients(!showMoreSubClients)"
                    >
                        {{ !showMoreSubClients ? $t("Show all") + " ("+client.subclients.length+")" : $t("Collapse") }}
                    </v-btn>
                </v-col>
            </template>

            <template v-else-if="row.field.name === 'parent_id' || row.field.type.startsWith('supplier.')">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <router-link :to="`/suppliers/${row.value}`">
                    {{ client[row.name + '_disp'] }}
                </router-link>
            </template>

            <template v-else-if="row.field.type === 'user_single' || row.field.type === 'user_multi'">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <v-col cols="7" :class="['value pb-0 pt-1', { 'mb-2': row.field.type === 'user_multi' && index < groupedField.fields.length - 1 }]">
                    <v-row v-for="member in getMembers(row.value)" :key="member.id" no-gutters>
                        <v-col class="shrink pb-1">
                            <PersonAvatar :person="member" :size="24" class-name="mr-2"></PersonAvatar>
                        </v-col>
                        <v-col class="pb-1">
                            <span class="body-2" style="line-height: 24px;">{{ member.name }}</span>
                        </v-col>
                    </v-row>
                </v-col>
            </template>

            <template v-else-if="row.field.name === 'cp_documents_url'">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <LinkButton :link="row.value" :host-only="true" />
            </template>

            <template v-else-if="row.field.name === 'cp_email'">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ $t('Email') }}
                </v-col>
                <v-col cols="7" class="value pb-1 pt-1" :test-id="`${row.name}-value`">
                    {{ row.field.value }}
                </v-col>
            </template>

            <template v-else-if="row.field.type === 'tax_id' && row.field.is_visible">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <v-col cols="7" class="value pb-1 pt-1" :test-id="`${row.name}-value`">
                    {{ defaultTaxName }}
                </v-col>
            </template>

            <template v-else-if="row.field.is_visible">
                <v-col cols="5" class="label pb-1 pt-1">
                    {{ row.field.title }}
                </v-col>
                <v-col cols="7" class="value pb-1 pt-1" :test-id="`${row.name}-value`">
                    {{ formatCustomFieldValue(row.value, row.field, client) }}
                </v-col>
            </template>

        </v-row>
    </v-card>
</template>

<script>
import AppModule from "@/store/modules/app";
import {computed, ref} from "@vue/composition-api";
import PersonAvatar from "../../components/PersonAvatar.vue";
import ClientAvatar from "../../components/ClientAvatar.vue";
import ClientContactsList from "../../components/Sidebar/Fields/ClientContactsList.vue";
import FieldGroupChilds from "../../components/CustomForm/FieldGroupChilds";
import ClientEmailsList from "@/views/Client/Overview/ClientEmailsList";
import {formatCustomFieldValue} from "../../composables/use-custom-fields";
import LinkButton from '../../components/Global/Input/Button/LinkButton.vue'
import {useMemberStore} from "../../store/modules/member";

export default {
    name: "ClientProfileFieldGroupCard",
    components: {
        LinkButton,
        ClientContactsList,
        PersonAvatar,
        FieldGroupChilds,
        ClientEmailsList,
        ClientAvatar
    },
    props: {
        groupedField: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        showSystemFields: {
            type: Boolean,
            default: true
        },
        topMargin: {
            type: Boolean,
            default: true
        }
    },
    setup(props) {
        const memberStore = useMemberStore()
        const {taxes} = AppModule();
        const showAllSubClientsKey = 'subclients:showall'
        const showMoreSubClients = ref(JSON.parse(localStorage.getItem(showAllSubClientsKey)))

        const filteredFields = computed(function () {
            const fieldsArray = props.groupedField.fields.filter(x => {
                // do not show if empty, unless it's parent_id in which case we do extra check in template
                if (!x.value && !(x.field.name === 'parent_id' && x.field.related_type === 'client')) return false
                if (['address_city', 'address_zip'].includes(x.name)) return false
                return !(Array.isArray(x.value) && !x.value.length)
            })

            // this is bad and I feel bad
            if(fieldsArray.find(x => x.name === 'cp_is_active' && x.value === 1 && props.client.cp_email)) {
                fieldsArray.push({field: {name: 'cp_email', type: 'text', value: props.client.cp_email}})
            }
            return fieldsArray
        })

        const getMembers = person_id_list => {
            if (Array.isArray(person_id_list)) {
                return memberStore.getMembersByIds(person_id_list)
            }

            return [memberStore.getMemberById(person_id_list)]
        };

        const getAddress = computed(() => {
            return [
                props.client.address_street,
                props.client.address_city,
                props.client.address_zip
            ].filter(x => x).join(', ')
        })

        const defaultTaxName = computed(() => {
            const defaultTaxId = props.client.tax_id
            const defaultTax = taxes.value[props.client.account_id].find((x) => x.id === defaultTaxId)
            return defaultTax ? defaultTax.name ? defaultTax.name : defaultTax.description ? defaultTax.description :'' :''
        })

        const subClients = computed(() => {
            return props.client.subclients ? showMoreSubClients.value ? props.client.subclients : props.client.subclients.slice(0, 3) : []
        })

        const toggleShowAllSubClients = (val) => {
            localStorage.setItem(showAllSubClientsKey, val)
            showMoreSubClients.value = val
        }

        return {
            getMembers,
            getAddress,
            filteredFields,
            formatCustomFieldValue,
            defaultTaxName,
            showMoreSubClients,
            subClients,
            toggleShowAllSubClients
        };
    }
};
</script>

<style scoped lang="scss">
.v-expansion-panels ::v-deep {
    .v-expansion-panel {
        &:before {
            display: none;
        }
    }

    .v-expansion-panel-content__wrap {
        padding: 0;
    }
}
</style>